import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'reactstrap';
import Navbar from './Navbar';
import Footer from './Footer';
import GuestChatWidget from './GuestChatWidget';
import './landing.scss';
import SeoHead from '../../../Components/Common/SeoHead';
import HelpBreadcrumbSchema from '../../../Components/Common/HelpBreadcrumbSchema';
import HelpScreenshot from './HelpScreenshot';
import type { HelpImage } from './HelpScreenshot';

const HelpArticleTemplates: React.FC = () => {
  const BASE = '/images/help/templates';

  const screenshots: Record<string, HelpImage[]> = {
    step1: [
      { src: `${BASE}/tmpl-step-1.png`, alt: 'Listing templates overview', caption: 'Step 1 – What listing templates are and why they matter' },
    ],
    step2: [
      { src: `${BASE}/tmpl-step-2.png`, alt: 'Template selection screen', caption: 'Step 2 – Browse and select a template' },
    ],
    step3: [
      { src: `${BASE}/tmpl-step-3.png`, alt: 'Template customisation interface', caption: 'Step 3 – Customise your template with logo, colours, and sections' },
    ],
    step4: [
      { src: `${BASE}/tmpl-step-4.png`, alt: 'Applying a template to products', caption: 'Step 4 – Apply a template to existing products' },
    ],
    step5: [
      { src: `${BASE}/tmpl-step-5.png`, alt: 'Template settings and defaults', caption: 'Step 5 – Configure template settings and defaults' },
    ],
  };

  return (
    <div className="layout-wrapper landing">
      <SeoHead
        title="Professional Listing Templates | CodeRom Help"
        description="Customise eBay listing templates for a professional storefront with CodeRom."
      />
      <HelpBreadcrumbSchema slug="templates" name="Professional listing templates" />
      <Navbar />

      <div className="help-article-page">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9} xl={8}>
              {/* Breadcrumb */}
              <div className="breadcrumb-section">
                <Link to="/help" className="breadcrumb-link">Help Center</Link>
                <span className="breadcrumb-separator">›</span>
                <span className="breadcrumb-current">Professional listing templates</span>
              </div>

              {/* Article Header */}
              <div className="article-header">
                <h1>Professional listing templates</h1>
                <p className="article-subtitle">
                  Create professional-looking eBay listings with Coderom's built-in templates.
                  Customise colours, layouts, and branding to make your products stand out.
                </p>
                <div className="article-meta">
                  <span className="read-time">📖 5 min read</span>
                  <span className="category">Templates</span>
                </div>
              </div>

              {/* Table of Contents */}
              <div className="table-of-contents">
                <h3>📋 On this page</h3>
                <ul>
                  <li><a href="#step1">1. What are listing templates?</a></li>
                  <li><a href="#step2">2. Choose a template</a></li>
                  <li><a href="#step3">3. Customise your template</a></li>
                  <li><a href="#step4">4. Apply a template to your products</a></li>
                  <li><a href="#step5">5. Template settings</a></li>
                </ul>
              </div>

              {/* Article Content */}
              <div className="article-content">

                {/* Step 1 */}
                <div id="step1" className="content-section">
                  <h2>Step 1: What are listing templates?</h2>
                  <p>
                    Listing templates control how your product description looks on eBay. Instead of
                    plain text, your listings get a professional, branded layout with:
                  </p>
                  <ul>
                    <li>Your store logo and name</li>
                    <li>Structured product details</li>
                    <li>Clean, mobile-friendly design</li>
                    <li>Consistent branding across all your listings</li>
                  </ul>
                  <p>
                    A good template builds buyer trust and can increase your conversion rate.
                  </p>

                  <HelpScreenshot images={screenshots.step1} stepLabel="Step 1: What are listing templates?" />
                </div>

                {/* Step 2 */}
                <div id="step2" className="content-section">
                  <h2>Step 2: Choose a template</h2>
                  <ol>
                    <li>Go to <strong>Settings → Listing Templates</strong> from the sidebar</li>
                    <li>Browse the available template designs</li>
                    <li>Click <strong>Preview</strong> on any template to see how it looks with sample data</li>
                    <li>Click <strong>Select</strong> to choose your template</li>
                  </ol>

                  <HelpScreenshot images={screenshots.step2} stepLabel="Step 2: Choose a template" />

                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Tip:</strong> Each template is fully responsive — it looks great on both
                      desktop and mobile. eBay has strict rules about HTML in listings, and all Coderom
                      templates comply with these rules.
                    </div>
                  </div>
                </div>

                {/* Step 3 */}
                <div id="step3" className="content-section">
                  <h2>Step 3: Customise your template</h2>
                  <p>
                    After selecting a template, you can personalise it:
                  </p>
                  <ol>
                    <li>Click <strong>Customise</strong> on your selected template</li>
                    <li>Upload your store logo</li>
                    <li>Set your brand colours (primary and secondary)</li>
                    <li>Add a store description or tagline</li>
                    <li>Choose which product sections to show (features, specifications, shipping info)</li>
                    <li>Click <strong>Save</strong></li>
                  </ol>

                  <HelpScreenshot images={screenshots.step3} stepLabel="Step 3: Customise your template" />

                  <div className="customisation-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Customisation</th>
                          <th>Description</th>
                          <th>Example</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Store logo</strong></td>
                          <td>Your brand logo displayed at the top</td>
                          <td>200x60px PNG recommended</td>
                        </tr>
                        <tr>
                          <td><strong>Primary colour</strong></td>
                          <td>Used for headers and borders</td>
                          <td>#2563eb (blue)</td>
                        </tr>
                        <tr>
                          <td><strong>Secondary colour</strong></td>
                          <td>Used for accents and buttons</td>
                          <td>#f59e0b (amber)</td>
                        </tr>
                        <tr>
                          <td><strong>Store tagline</strong></td>
                          <td>Short text below your logo</td>
                          <td>"Premium electronics at the best prices"</td>
                        </tr>
                        <tr>
                          <td><strong>Sections</strong></td>
                          <td>Toggle visibility of each section</td>
                          <td>Features, Specs, Shipping, Returns</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Step 4 */}
                <div id="step4" className="content-section">
                  <h2>Step 4: Apply a template to your products</h2>
                  <p>
                    Templates are applied automatically to new listings. For existing listings:
                  </p>
                  <ol>
                    <li>Go to <strong>Draft Products</strong> or <strong>Active Products</strong></li>
                    <li>Select the products you want to update</li>
                    <li>Click <strong>Apply Template</strong></li>
                    <li>Choose your template</li>
                    <li>Click <strong>Apply</strong> — Coderom will update the descriptions on eBay</li>
                  </ol>

                  <HelpScreenshot images={screenshots.step4} stepLabel="Step 4: Apply a template to your products" />

                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Tip:</strong> Applying a template to active listings will revise them on
                      eBay automatically. The product information stays the same — only the visual layout changes.
                    </div>
                  </div>
                </div>

                {/* Step 5 */}
                <div id="step5" className="content-section">
                  <h2>Step 5: Template settings</h2>
                  <p>
                    Go to <strong>Settings → Listing Templates</strong> to manage your template preferences:
                  </p>

                  <div className="settings-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Setting</th>
                          <th>What it does</th>
                          <th>Recommended</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Default template</strong></td>
                          <td>Automatically applied to all new listings</td>
                          <td>Choose your favourite</td>
                        </tr>
                        <tr>
                          <td><strong>Auto-apply on publish</strong></td>
                          <td>Apply template when publishing drafts</td>
                          <td>On</td>
                        </tr>
                        <tr>
                          <td><strong>Include shipping info</strong></td>
                          <td>Show shipping details in the template</td>
                          <td>On</td>
                        </tr>
                        <tr>
                          <td><strong>Include returns policy</strong></td>
                          <td>Show your returns policy</td>
                          <td>On</td>
                        </tr>
                        <tr>
                          <td><strong>Show store branding</strong></td>
                          <td>Display your logo and store name</td>
                          <td>On</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <HelpScreenshot images={screenshots.step5} stepLabel="Step 5: Template settings" />
                </div>

                {/* What's Next */}
                <div className="whats-next-section">
                  <h2>What's next?</h2>
                  <p>Now that your listings look professional, explore these features:</p>
                  <div className="next-steps">
                    <Link to="/help/pricing" className="next-step-card">
                      <div className="next-step-icon">💰</div>
                      <div className="next-step-content">
                        <h4>Smart repricing & pricing rules</h4>
                        <p>Automatically adjust prices based on Amazon changes and market conditions</p>
                      </div>
                    </Link>
                    <Link to="/help/stock" className="next-step-card">
                      <div className="next-step-icon">📦</div>
                      <div className="next-step-content">
                        <h4>Stock synchronisation & inventory</h4>
                        <p>Keep your eBay listings in sync with Amazon stock levels 24/7</p>
                      </div>
                    </Link>
                    <Link to="/help/amazon-filters" className="next-step-card">
                      <div className="next-step-icon">🔍</div>
                      <div className="next-step-content">
                        <h4>Amazon supplier filters</h4>
                        <p>Set quality rules to automatically select the best Amazon offers</p>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Was This Helpful */}
                <div className="helpful-section">
                  <h3>Was this helpful?</h3>
                  <div className="helpful-buttons">
                    <button className="helpful-btn helpful-yes">👍 Yes</button>
                    <button className="helpful-btn helpful-no">👎 No</button>
                  </div>
                  <p className="helpful-text">
                    Still need help? <Link to="/help">Browse all articles</Link> or{' '}
                    <a href="mailto:support@coderom.com">contact support</a>.
                  </p>
                </div>

              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <style>{`
        .help-article-page {
          padding: 120px 0 80px;
          background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
          min-height: 100vh;
        }

        /* Breadcrumb */
        .breadcrumb-section {
          margin-bottom: 24px;
          font-size: 14px;
        }
        .breadcrumb-link {
          color: #6366f1;
          text-decoration: none;
          transition: color 0.2s;
        }
        .breadcrumb-link:hover {
          color: #4f46e5;
        }
        .breadcrumb-separator {
          margin: 0 8px;
          color: #cbd5e1;
        }
        .breadcrumb-current {
          color: #64748b;
        }

        /* Article Header */
        .article-header {
          margin-bottom: 40px;
        }
        .article-header h1 {
          font-size: 42px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          line-height: 1.2;
        }
        .article-subtitle {
          font-size: 18px;
          color: #64748b;
          line-height: 1.6;
          margin-bottom: 20px;
        }
        .article-meta {
          display: flex;
          gap: 16px;
          align-items: center;
          font-size: 14px;
        }
        .read-time {
          color: #64748b;
        }
        .category {
          background: #f1f5f9;
          color: #6366f1;
          padding: 4px 12px;
          border-radius: 12px;
          font-weight: 500;
        }

        /* Table of Contents */
        .table-of-contents {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 40px;
        }
        .table-of-contents h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 16px;
        }
        .table-of-contents ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .table-of-contents li {
          margin-bottom: 8px;
        }
        .table-of-contents a {
          color: #6366f1;
          text-decoration: none;
          font-size: 15px;
          transition: color 0.2s;
        }
        .table-of-contents a:hover {
          color: #4f46e5;
          text-decoration: underline;
        }

        /* Article Content */
        .article-content {
          color: #334155;
          font-size: 16px;
          line-height: 1.8;
        }
        .content-section {
          margin-bottom: 48px;
          scroll-margin-top: 100px;
        }
        .content-section h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          margin-top: 40px;
        }
        .content-section h3 {
          font-size: 20px;
          font-weight: 600;
          color: #334155;
          margin-top: 24px;
          margin-bottom: 12px;
        }
        .content-section p {
          margin-bottom: 16px;
        }
        .content-section ul,
        .content-section ol {
          margin-bottom: 16px;
          padding-left: 24px;
        }
        .content-section li {
          margin-bottom: 8px;
        }
        .content-section strong {
          color: #1e293b;
          font-weight: 600;
        }

        /* Tip Box */
        .tip-box {
          display: flex;
          gap: 12px;
          background: #eff6ff;
          border-left: 4px solid #3b82f6;
          padding: 16px;
          border-radius: 8px;
          margin: 24px 0;
        }
        .tip-icon {
          font-size: 20px;
          flex-shrink: 0;
        }
        .tip-content {
          font-size: 15px;
          line-height: 1.6;
          color: #1e40af;
        }

        /* Tables */
        .customisation-table,
        .settings-table {
          margin: 24px 0;
          overflow-x: auto;
        }
        .customisation-table table,
        .settings-table table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .customisation-table th,
        .settings-table th {
          background: #f8fafc;
          color: #1e293b;
          font-weight: 600;
          text-align: left;
          padding: 12px 16px;
          border-bottom: 2px solid #e2e8f0;
          font-size: 14px;
        }
        .customisation-table td,
        .settings-table td {
          padding: 12px 16px;
          border-bottom: 1px solid #f1f5f9;
          font-size: 15px;
        }
        .customisation-table tbody tr:last-child td,
        .settings-table tbody tr:last-child td {
          border-bottom: none;
        }
        .customisation-table tbody tr:hover,
        .settings-table tbody tr:hover {
          background: #f8fafc;
        }

        /* What's Next Section */
        .whats-next-section {
          margin-top: 60px;
          padding-top: 40px;
          border-top: 2px solid #e2e8f0;
        }
        .whats-next-section h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
        }
        .next-steps {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-top: 24px;
        }
        .next-step-card {
          display: flex;
          gap: 16px;
          padding: 20px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          text-decoration: none;
          transition: all 0.3s;
        }
        .next-step-card:hover {
          border-color: #6366f1;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
          transform: translateY(-2px);
        }
        .next-step-icon {
          font-size: 32px;
          flex-shrink: 0;
        }
        .next-step-content h4 {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 4px;
        }
        .next-step-content p {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }

        /* Was This Helpful */
        .helpful-section {
          margin-top: 60px;
          padding: 32px;
          background: #f8fafc;
          border-radius: 12px;
          text-align: center;
        }
        .helpful-section h3 {
          font-size: 20px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 20px;
        }
        .helpful-buttons {
          display: flex;
          gap: 12px;
          justify-content: center;
          margin-bottom: 16px;
        }
        .helpful-btn {
          padding: 10px 24px;
          border: 2px solid #e2e8f0;
          background: white;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        .helpful-btn:hover {
          border-color: #6366f1;
          background: #eff6ff;
        }
        .helpful-text {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }
        .helpful-text a {
          color: #6366f1;
          text-decoration: none;
        }
        .helpful-text a:hover {
          text-decoration: underline;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .help-article-page {
            padding: 100px 0 60px;
          }
          .article-header h1 {
            font-size: 32px;
          }
          .article-subtitle {
            font-size: 16px;
          }
          .content-section h2 {
            font-size: 24px;
          }
          .customisation-table,
          .settings-table {
            font-size: 14px;
          }
          .next-step-card {
            flex-direction: column;
            text-align: center;
          }
        }
      `}</style>

      <Footer />
      <GuestChatWidget />
    </div>
  );
};

export default HelpArticleTemplates;
